// Single shared OL Finance report catalog + nonprofit naming, used by the
// Treasurer Reports screen and the read-only FinanceViewer so labels never
// diverge. Names/sections are derived here (one map), never per screen.

export const PRESETS = [
  ['all', 'All Dates'], ['custom', 'Custom'],
  ['today', 'Today'],
  ['this_week', 'This week'], ['this_week_to_date', 'This week-to-date'],
  ['this_month', 'This month'], ['this_month_to_date', 'This month-to-date'],
  ['this_quarter', 'This quarter'], ['this_quarter_to_date', 'This quarter-to-date'],
  ['this_year', 'This year'], ['this_year_to_date', 'This year-to-date'],
  ['this_fiscal_year', 'This fiscal year'], ['this_fiscal_year_to_date', 'This fiscal year-to-date'],
  ['last_week', 'Last week'], ['last_month', 'Last month'], ['last_quarter', 'Last quarter'],
  ['last_year', 'Last year'], ['last_fiscal_year', 'Last fiscal year'],
  ['last_7_days', 'Last 7 days'], ['last_30_days', 'Last 30 days'],
  ['last_90_days', 'Last 90 days'], ['last_365_days', 'Last 365 days'],
  ['since_30_days_ago', 'Since 30 days ago'], ['since_60_days_ago', 'Since 60 days ago'],
  ['since_90_days_ago', 'Since 90 days ago'], ['since_365_days_ago', 'Since 365 days ago'],
];
export const PRESET_LABEL = Object.fromEntries(PRESETS);

export const COLUMN_OPTIONS = [
  ['total', 'Total'], ['month', 'Months'], ['quarter', 'Quarters'], ['year', 'Years'],
  ['customer', 'Customers'], ['vendor', 'Vendors'], ['class', 'Tags'],
];
export const COMPARE_OPTIONS = [['none', 'None'], ['prev_period', 'Previous period'], ['prev_year', 'Previous year']];

export const isNonprofit = (c) =>
  !!c && (String(c.tax_form || '').startsWith('990')
    || c.starter_chart === 'nonprofit'
    || /non.?profit|990/i.test(String(c.business_type || '')));

// One section-naming map: account type -> section header (nonprofit vs standard).
export function sectionMap(np) {
  return {
    Income: np ? 'Revenue' : 'Income',
    'Cost of Goods Sold': 'Cost of Goods Sold',
    Expense: np ? 'Expenditures' : 'Expenses',
    'Other Income': np ? 'Other Revenue' : 'Other Income',
    'Other Expense': np ? 'Other Expenditures' : 'Other Expenses',
  };
}
export function netLabels(np) {
  return {
    grossProfit: 'Gross Profit',
    netOperating: np ? 'Net Operating Revenue' : 'Net Operating Income',
    net: np ? 'Net Revenue' : 'Net Income',
  };
}
// section render order (P&L then Balance Sheet types)
export const TYPE_ORDER = [
  'Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense',
  'Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Other Asset',
  'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability', 'Equity',
];

export function catalog(np) {
  const SoA = np ? 'Statement of Activity' : 'Profit and Loss';
  const SoFP = np ? 'Statement of Financial Position' : 'Balance Sheet';
  return [
    // ── Nonprofit & 1099 (501(c)(3) essentials — olfin/nonprofit.py)
    { k: 'functional_expenses', g: 'Nonprofit & 1099', label: 'Statement of Functional Expenses' },
    { k: 'contributions_by_donor', g: 'Nonprofit & 1099', label: 'Contributions by Donor' },
    { k: 'form_990_summary', g: 'Nonprofit & 1099', label: 'Form 990 Summary' },
    { k: 'profit_loss_by_class', g: 'Nonprofit & 1099', label: `${SoA} by Tag (sport)` },
    { k: 'possible_1099_vendors', g: 'Nonprofit & 1099', label: 'Possible 1099 Vendors' },
    { k: '1099_summary', id: 'np_1099_summary', g: 'Nonprofit & 1099', label: '1099 Contractor Summary' },
    // ── Optional features' reports (shown only when the feature is on)
    { k: 'sales_tax_liability', g: 'Sales and customers', label: 'Sales Tax Liability', feature: 'sales_tax' },
    { k: 'sales_tax_revenue_summary', g: 'Sales and customers', label: 'Sales Tax Revenue Summary', feature: 'sales_tax' },
    { k: 'inventory_valuation_summary', g: 'Sales and customers', label: 'Inventory Valuation Summary', feature: 'inventory' },
    { k: 'inventory_valuation_detail', g: 'Sales and customers', label: 'Inventory Valuation Detail', feature: 'inventory' },
    { k: 'inventory_stock_status', g: 'Sales and customers', label: 'Inventory Stock Status', feature: 'inventory' },
    { k: 'physical_inventory_worksheet', g: 'Sales and customers', label: 'Physical Inventory Worksheet', feature: 'inventory' },
    { k: 'payroll_summary', g: 'Employees', label: 'Payroll Summary', feature: 'payroll' },
    { k: 'payroll_liability_balances', g: 'Employees', label: 'Payroll Liability Balances', feature: 'payroll' },
    { k: 'form_941', g: 'Employees', label: 'Form 941 Worksheet (quarter)', feature: 'payroll' },
    { k: 'form_940', g: 'Employees', label: 'Form 940 Worksheet (year)', feature: 'payroll' },
    // ── Business overview
    { k: 'audit_trail', g: 'Business overview', label: 'Audit Log' },
    { k: 'balance_sheet', g: 'Business overview', label: SoFP },
    { k: 'balance_sheet_prev_year', g: 'Business overview', label: `${SoFP} Comparison` },
    { k: 'balance_sheet_detail', g: 'Business overview', label: `${SoFP} Detail` },
    { k: 'balance_sheet', id: 'sofp_summary', params: { summary: true }, g: 'Business overview', label: `${SoFP} Summary` },
    { k: 'budget_overview', g: 'Business overview', label: 'Budget Overview' },
    { k: 'budget_vs_actual', g: 'Business overview', label: 'Budget vs. Actuals' },
    { k: 'cash_flows', g: 'Business overview', label: 'Statement of Cash Flows' },
    { k: 'business_snapshot', g: 'Business overview', label: 'Business Snapshot' },
    { k: 'profit_loss', g: 'Business overview', label: SoA },
    { k: 'profit_loss_by_customer', g: 'Business overview', label: `${SoA} by Customer` },
    { k: 'profit_loss', id: 'soa_month', params: { columns: 'month' }, g: 'Business overview', label: `${SoA} by Month` },
    { k: 'profit_loss', id: 'soa_tag', params: { columns: 'class' }, g: 'Business overview', label: `${np ? 'Statement of Activity' : 'Profit and Loss'} by Tag Group` },
    { k: 'profit_loss_prev_year', g: 'Business overview', label: `${SoA} Comparison` },
    { k: 'profit_loss_detail', g: 'Business overview', label: `${SoA} Detail` },
    { k: 'profit_loss_pct', g: 'Business overview', label: `${SoA} as % of total income` },
    { k: 'profit_loss_prev_year', id: 'soa_ytd', params: { preset: 'this_fiscal_year_to_date' }, g: 'Business overview', label: `${SoA} year-to-date comparison` },
    { k: 'profit_loss', id: 'soa_quarter', params: { columns: 'quarter' }, g: 'Business overview', label: `Quarterly ${np ? 'Statement of Activity' : 'Profit and Loss'} Summary` },
    // ── Who owes you
    { k: 'ar_aging_summary', g: 'Who owes you', label: 'A/R Aging Summary' },
    { k: 'ar_aging_detail', g: 'Who owes you', label: 'A/R Aging Detail' },
    { k: 'collections_report', g: 'Who owes you', label: 'Collections Report' },
    { k: 'customer_balance_summary', g: 'Who owes you', label: 'Customer Balance Summary' },
    { k: 'customer_balance_detail', g: 'Who owes you', label: 'Customer Balance Detail' },
    { k: 'invoice_list', g: 'Who owes you', label: 'Invoice List' },
    { k: 'pledges_and_received_payments', g: 'Who owes you', label: 'Pledges and Received Payments' },
    { k: 'open_invoices', g: 'Who owes you', label: 'Open Invoices' },
    { k: 'statement_list', g: 'Who owes you', label: 'Statement List' },
    { k: 'terms_list', g: 'Who owes you', label: 'Terms List' },
    { k: 'unbilled_charges', g: 'Who owes you', label: 'Unbilled charges' },
    { k: 'family_balances', g: 'Who owes you', label: 'Family Balances' },
    // ── Sales and customers
    { k: 'cashflow_payment_transactions', g: 'Sales and customers', label: 'Cashflow Payment Transactions' },
    { k: 'sales_by_customer_detail', id: 'sales_by_cust_type_detail', g: 'Sales and customers', label: 'Sales by Customer Type Detail' },
    { k: 'customer_contact_list', g: 'Sales and customers', label: 'Customer Contact List' },
    { k: 'sales_by_customer_summary', id: 'income_by_customer_summary', g: 'Sales and customers', label: 'Income by Customer Summary' },
    { k: 'customer_phone_list', g: 'Sales and customers', label: 'Customer Phone List' },
    { k: 'sales_by_customer_summary', g: 'Sales and customers', label: 'Sales by Customer Summary' },
    { k: 'sales_by_customer_detail', g: 'Sales and customers', label: 'Sales by Customer Detail' },
    { k: 'deposit_detail', g: 'Sales and customers', label: 'Deposit Detail' },
    { k: 'product_service_list', g: 'Sales and customers', label: 'Product/Service List' },
    { k: 'sales_by_product_service_summary', g: 'Sales and customers', label: 'Sales by Product/Service Summary' },
    { k: 'sales_by_product_service_detail', g: 'Sales and customers', label: 'Sales by Product/Service Detail' },
    { k: 'payment_method_list', g: 'Sales and customers', label: 'Payment Method List' },
    { k: 'profit_loss_by_customer', id: 'txn_list_by_customer', g: 'Sales and customers', label: 'Transaction List by Customer' },
    { k: 'profit_loss', id: 'txn_list_by_tag', params: { columns: 'class' }, g: 'Sales and customers', label: 'Transaction List by Tag Group' },
    // ── What you owe
    { k: 'ap_aging_summary', g: 'What you owe', label: 'A/P Aging Summary' },
    { k: 'ap_aging_detail', g: 'What you owe', label: 'A/P Aging Detail' },
    { k: 'bills_and_applied_payments', g: 'What you owe', label: 'Bills and Applied Payments' },
    { k: 'bill_payment_list', g: 'What you owe', label: 'Bill Payment List' },
    { k: '1099_summary', g: 'What you owe', label: '1099 Contractor Balance Summary' },
    { k: 'unpaid_bills_detail', g: 'What you owe', label: 'Unpaid Bills' },
    { k: 'vendor_balance_summary', g: 'What you owe', label: 'Vendor Balance Summary' },
    { k: 'vendor_balance_detail', g: 'What you owe', label: 'Vendor Balance Detail' },
    // ── Expenses and vendors
    { k: 'expenses_by_vendor_summary', g: 'Expenses and vendors', label: 'Expenses by Vendor Summary' },
    { k: 'expenses_by_vendor_detail', g: 'Expenses and vendors', label: 'Expenses by Vendor Detail' },
    { k: 'purchases_by_vendor_detail', g: 'Expenses and vendors', label: 'Purchases by Vendor Detail' },
    { k: 'purchases_by_product_service_detail', g: 'Expenses and vendors', label: 'Purchases by Product/Service Detail' },
    { k: 'expenses_by_vendor_detail', id: 'txn_list_by_vendor', g: 'Expenses and vendors', label: 'Transaction List by Vendor' },
    { k: 'vendor_contact_list', g: 'Expenses and vendors', label: 'Vendor Contact List' },
    { k: 'vendor_phone_list', g: 'Expenses and vendors', label: 'Vendor Phone List' },
    { k: 'check_detail', g: 'Expenses and vendors', label: 'Check Detail' },
    // ── Employees
    { k: 'employee_contact_list', g: 'Employees', label: 'Employee Contact List' },
    // ── For my accountant
    { k: 'account_list', g: 'For my accountant', label: 'Account List' },
    { k: 'balance_sheet', id: 'acct_sofp', g: 'For my accountant', label: SoFP },
    { k: 'cash_flows', id: 'acct_scf', g: 'For my accountant', label: 'Statement of Cash Flows' },
    { k: 'invalid_journal_transactions', g: 'For my accountant', label: 'Invalid Journal Transactions' },
    { k: 'general_ledger', g: 'For my accountant', label: 'General Ledger' },
    { k: 'general_ledger', id: 'gl_list', g: 'For my accountant', label: 'General Ledger List' },
    { k: 'journal', g: 'For my accountant', label: 'Journal' },
    { k: 'recurring_template_list', g: 'For my accountant', label: 'Recurring Template List' },
    { k: 'profit_loss', id: 'acct_soa', g: 'For my accountant', label: SoA },
    { k: 'recent_transactions', g: 'For my accountant', label: 'Recent Transactions' },
    { k: 'reconciliation_list', g: 'For my accountant', label: 'Reconciliation Reports' },
    { k: 'trial_balance', g: 'For my accountant', label: 'Trial Balance' },
    { k: 'adjusted_trial_balance', g: 'For my accountant', label: 'Adjusted Trial Balance' },
    { k: 'adjusting_journal_entries', g: 'For my accountant', label: 'Adjusting Journal Entries' },
    { k: 'transaction_detail_by_account', g: 'For my accountant', label: 'Transaction Detail by Account' },
    { k: 'transaction_list_by_date', g: 'For my accountant', label: 'Transaction List by Date' },
    { k: 'transaction_list_with_splits', g: 'For my accountant', label: 'Transaction List with Splits' },
  ].map((d) => ({ ...d, id: d.id || d.k }));
}

export const GROUP_ORDER = ['Favorites', 'Nonprofit & 1099', 'Business overview', 'Who owes you', 'Sales and customers',
  'What you owe', 'Expenses and vendors', 'Employees', 'For my accountant'];

// Packaged Management report = cover + these, as one PDF.
export const MANAGEMENT_REPORTS = ['profit_loss', 'balance_sheet', 'ar_aging_summary', 'ap_aging_summary'];

// Compact subset surfaced as quick buttons in the read-only FinanceViewer.
export const VIEWER_REPORT_KEYS = ['profit_loss', 'balance_sheet', 'trial_balance', 'ar_aging_summary', 'family_balances'];

// The base report set (feature "all_reports" off): what a sports nonprofit's Treasurer and board use.
export const ESSENTIALS = new Set(['functional_expenses', 'contributions_by_donor', 'form_990_summary', 'profit_loss_by_class',
  'possible_1099_vendors', 'np_1099_summary', 'profit_loss', 'profit_loss_prev_year', 'balance_sheet', 'budget_vs_actual',
  'cash_flows', 'deposit_detail', 'check_detail', 'expenses_by_vendor_summary', 'ar_aging_summary', 'family_balances',
  'transaction_list_by_date', 'general_ledger', 'reconciliation_list', 'audit_trail']);
